<template>
  <div id="admin">
    <div class="container pt-3">
      <h1 class="text-center">Every.Farm Admin Dashboard</h1>

      <!-- Defense-in-depth client-side gate. The router guard is the
           authoritative check (and the Cloud Function double-verifies
           server-side), but if anything ever lets a non-admin's
           browser load this component, render a polite redirect
           rather than a stack of API errors. -->
      <div v-if="!$store.state.isSuperAdmin" class="text-center mt-5">
        <p class="lead">This page is for platform administrators only.</p>
        <router-link to="/">Return to your farms →</router-link>
      </div>

      <template v-else>
        <!-- Original totals panel (users / farms / datasets) -->
        <Totals
          v-if="legacyTotals"
          :usersTotal="legacyTotals.users"
          :farmsTotal="legacyTotals.farms"
          :datasetsTotal="legacyTotals.datasets"
        />

        <!-- =================== USAGE OVERVIEW =================== -->
        <div class="admin-card mt-4">
          <h2><i class="fas fa-chart-line"></i> Platform Usage</h2>
          <p v-if="usageLoading" class="text-muted">
            <i class="fas fa-spinner fa-spin"></i> Loading usage stats…
          </p>
          <div v-else-if="usageError" class="text-warning">
            {{ usageError }}
          </div>
          <div v-else-if="usage">
            <!-- Summary number row -->
            <div class="stat-grid">
              <div class="stat-cell">
                <div class="stat-num">{{ formatN(usage.summary.dau) }}</div>
                <div class="stat-label">DAU (24h)</div>
              </div>
              <div class="stat-cell">
                <div class="stat-num">{{ formatN(usage.summary.wau) }}</div>
                <div class="stat-label">WAU (7d)</div>
              </div>
              <div class="stat-cell">
                <div class="stat-num">{{ formatN(usage.summary.mau) }}</div>
                <div class="stat-label">MAU (30d)</div>
              </div>
              <div class="stat-cell">
                <div class="stat-num">{{ formatN(usage.summary.userCount) }}</div>
                <div class="stat-label">Tracked users</div>
              </div>
              <div class="stat-cell">
                <div class="stat-num">{{ formatN(usage.summary.totalCredits) }}</div>
                <div class="stat-label">Lifetime credits</div>
              </div>
              <div class="stat-cell">
                <div class="stat-num">{{ formatN(usage.summary.totalOperations) }}</div>
                <div class="stat-label">Lifetime operations</div>
              </div>
              <div class="stat-cell">
                <div class="stat-num">{{ formatBytes(usage.summary.totalBytesUploaded) }}</div>
                <div class="stat-label">Lifetime uploads</div>
              </div>
            </div>

            <!-- Plan distribution — prep for paid-tier rollout -->
            <p class="mt-3 mb-1"><strong>Plans:</strong>
              <span v-for="(count, plan) in usage.planCounts" :key="plan" class="plan-pill">
                {{ plan }} · {{ count }}
              </span>
            </p>
          </div>
        </div>

        <!-- =================== DAILY TREND =================== -->
        <div v-if="usage" class="admin-card mt-3">
          <h2><i class="fas fa-calendar-alt"></i> Last 30 Days</h2>
          <p class="text-muted small mb-2">
            Daily credit consumption and active users (UTC days).
          </p>
          <div class="trend-rows">
            <div
              v-for="row in usage.daily"
              :key="row.date"
              class="trend-row"
            >
              <div class="trend-date">{{ row.date }}</div>
              <div class="trend-bar">
                <div
                  class="trend-bar-fill"
                  :style="{ width: barWidth(row.credits) + '%' }"
                ></div>
              </div>
              <div class="trend-stats">
                <span>{{ formatN(row.credits) }} cr</span>
                <span class="trend-sub">
                  · {{ formatN(row.operations) }} ops
                  · {{ row.activeUsers }} active
                  <span v-if="row.bytes > 0">· {{ formatBytes(row.bytes) }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- =================== TOP OPERATIONS =================== -->
        <div v-if="usage && usage.topOperations30d.length" class="admin-card mt-3">
          <h2><i class="fas fa-list-ol"></i> Top Operations (30d)</h2>
          <p class="text-muted small mb-2">
            Which features are users actually getting value from?
            "Bytes" only populates for upload-shaped operations.
          </p>
          <table class="admin-table">
            <thead>
              <tr>
                <th>Operation</th>
                <th class="num">Credits</th>
                <th class="num">Count</th>
                <th class="num">Bytes</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="op in usage.topOperations30d" :key="op.operation">
                <td>{{ op.label }}</td>
                <td class="num">{{ formatN(op.credits) }}</td>
                <td class="num">{{ formatN(op.count) }}</td>
                <td class="num">{{ op.bytes > 0 ? formatBytes(op.bytes) : '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- =================== TOP STORAGE USERS =================== -->
        <div v-if="usage && usage.topStorageUsers && usage.topStorageUsers.length" class="admin-card mt-3">
          <h2><i class="fas fa-database"></i> Top Storage Users (lifetime)</h2>
          <p class="text-muted small mb-2">
            A different cost shape than heavy-compute users — storage is
            perpetual. Watch for users uploading huge raster archives.
          </p>
          <table class="admin-table">
            <thead><tr><th>User</th><th>Email</th><th class="num">Uploaded (lifetime)</th></tr></thead>
            <tbody>
              <tr v-for="row in usage.topStorageUsers" :key="row.uid">
                <td>
                  {{ (row.userProfile && (row.userProfile.firstName || row.userProfile.lastName))
                      ? `${row.userProfile.firstName || ''} ${row.userProfile.lastName || ''}`.trim()
                      : '—' }}
                  <small v-if="row.userProfile && row.userProfile.company" class="text-muted">
                    · {{ row.userProfile.company }}
                  </small>
                </td>
                <td>{{ (row.userProfile && row.userProfile.email) || '—' }}</td>
                <td class="num">{{ formatBytes(row.lifetimeBytes) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- =================== TOP USERS =================== -->
        <div v-if="usage && usage.topUsers30d.length" class="admin-card mt-3">
          <h2><i class="fas fa-trophy"></i> Top Users (30d)</h2>
          <p class="text-muted small mb-2">
            By credit consumption — useful both for "who's our heaviest user"
            and as a rate-abuse smoke signal.
          </p>
          <table class="admin-table">
            <thead>
              <tr>
                <th>User</th><th>Email</th>
                <th class="num">Credits</th><th class="num">Ops</th>
                <th class="num">Bytes (30d)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in usage.topUsers30d" :key="row.uid">
                <td>
                  {{ (row.userProfile && (row.userProfile.firstName || row.userProfile.lastName))
                      ? `${row.userProfile.firstName || ''} ${row.userProfile.lastName || ''}`.trim()
                      : '—' }}
                  <small v-if="row.userProfile && row.userProfile.company" class="text-muted">
                    · {{ row.userProfile.company }}
                  </small>
                </td>
                <td>{{ (row.userProfile && row.userProfile.email) || '—' }}</td>
                <td class="num">{{ formatN(row.credits) }}</td>
                <td class="num">{{ formatN(row.operations) }}</td>
                <td class="num">{{ row.bytes > 0 ? formatBytes(row.bytes) : '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- =================== FARMS MAP =================== -->
        <div class="admin-card mt-3">
          <h2><i class="fas fa-map"></i> Farms Map</h2>
          <FarmsMap />
        </div>

        <!-- =================== FARM MANAGEMENT =================== -->
        <div class="admin-card mt-3">
          <h2><i class="fas fa-seedling"></i> Farm Management</h2>
          <p class="text-muted small mb-2">
            Search a farm to view and edit per-farm settings.
          </p>
          <FarmManager />
        </div>

        <!-- =================== EMAIL UPDATE TO USERS =================== -->
        <div class="admin-card mt-3">
          <h2><i class="fas fa-envelope"></i> Email Update to Users</h2>
          <p class="text-muted small mb-2">
            Send an occasional update to every user who hasn't opted out of
            <em>Product updates &amp; announcements</em>. Account notifications
            (invites, processing results) are unaffected. Every email carries a
            one-click unsubscribe link.
          </p>

          <div class="form-group">
            <label><strong>Subject</strong></label>
            <input v-model="broadcast.subject" class="form-control" maxlength="150" placeholder="What's new on Every.Farm" />
          </div>
          <div class="form-group">
            <label><strong>Message</strong></label>
            <textarea v-model="broadcast.message" class="form-control" rows="6" placeholder="Write your update — plain text, line breaks are preserved."></textarea>
          </div>
          <div class="form-row">
            <div class="col-md-7 form-group">
              <label><strong>Button link <span class="text-muted">(optional)</span></strong></label>
              <input v-model="broadcast.ctaUrl" class="form-control" placeholder="https://app.every.farm/blog/posts/..." />
            </div>
            <div class="col-md-5 form-group">
              <label><strong>Button label</strong></label>
              <input v-model="broadcast.ctaLabel" class="form-control" placeholder="Read the post" />
            </div>
          </div>

          <div class="form-row align-items-end">
            <div class="col-md-6 form-group mb-0">
              <label><strong>Preview recipient</strong></label>
              <input v-model="previewTo" class="form-control" placeholder="you@every.farm" />
            </div>
            <div class="col-md-6 form-group mb-0">
              <b-btn
                variant="outline-secondary"
                class="mt-2"
                :disabled="!canSendBroadcast || sendingPreview"
                @click="sendPreview()"
              >
                <i class="fas fa-eye" v-if="!sendingPreview"></i>
                <i class="fas fa-spinner fa-spin" v-else></i>
                Send preview
              </b-btn>
            </div>
          </div>

          <div class="d-flex align-items-center mt-3">
            <b-btn
              variant="success"
              :disabled="!canSendBroadcast || sendingBroadcast"
              @click="confirmBroadcast = true"
            >
              <i class="fas fa-paper-plane" v-if="!sendingBroadcast"></i>
              <i class="fas fa-spinner fa-spin" v-else></i>
              Send to users
            </b-btn>
            <span class="text-muted small ml-3" v-if="broadcastResult">{{ broadcastResult }}</span>
          </div>

          <div v-if="recentBroadcasts.length" class="mt-3">
            <p class="mb-1"><strong>Recent sends</strong></p>
            <table class="table table-sm mb-0">
              <thead>
                <tr><th>Subject</th><th>Status</th><th>Sent</th><th>When</th></tr>
              </thead>
              <tbody>
                <tr v-for="bc in recentBroadcasts" :key="bc.id">
                  <td>{{ bc.subject }}</td>
                  <td><span class="badge" :class="broadcastBadge(bc.status)">{{ bc.status }}</span></td>
                  <td>
                    {{ bc.sent != null ? bc.sent : '—' }}<span v-if="bc.recipientCount != null"> / {{ bc.recipientCount }}</span><span v-if="bc.failed"> ({{ bc.failed }} failed)</span>
                  </td>
                  <td>{{ formatBroadcastTime(bc.createdOn) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- =================== EXPORTS =================== -->
        <div class="admin-card mt-3">
          <h2><i class="fas fa-download"></i> Exports</h2>
          <b-btn class="mr-2 mt-1" @click="downloadUsers()">
            <i class="fas fa-file-download"></i> User Emails CSV
          </b-btn>
          <b-btn class="mr-2 mt-1" @click="downloadGIS()">
            <i class="fas fa-file-download"></i> Farms GeoJSON
          </b-btn>
        </div>

        <p class="text-muted small mt-4 text-center" v-if="usage">
          Usage data generated at {{ usage.generatedAt }} ·
          <a href="#" @click.prevent="loadUsage(true)">Refresh</a>
        </p>
      </template>
    </div>
    <Confirm
      v-if="confirmBroadcast"
      text="Send this update to every user who hasn't opted out of product updates? This sends real email and can't be undone."
      v-on:confirmed="sendBroadcast($event)"
    />
  </div>
</template>

<script>
import FarmsMap from './FarmsMap'
import FarmManager from './FarmManager'
import Totals from './Totals'
import Confirm from '../Confirm'
import firebase from 'firebase/compat/app'
const fb = require('../../firebaseConfig.js')

export default {
  data () {
    return {
      legacyTotals: false,
      dailyStats: [],
      usage: null,
      usageLoading: false,
      usageError: null,
      broadcast: { subject: '', message: '', ctaUrl: '', ctaLabel: '' },
      previewTo: '',
      sendingPreview: false,
      sendingBroadcast: false,
      broadcastResult: '',
      confirmBroadcast: false,
      recentBroadcasts: [],
      broadcastsUnsub: null
    }
  },
  components: { FarmsMap, FarmManager, Totals, Confirm },
  computed: {
    // Used for bar width — scale every bar relative to the max credit
    // value in the 30-day window so a quiet week doesn't render as a
    // sea of zero-width bars.
    maxDailyCredits () {
      if (!this.usage || !this.usage.daily.length) return 1
      return Math.max(1, ...this.usage.daily.map(d => d.credits))
    },
    // Gate the "Send to users" button: both a subject and a message are
    // required before we'll let a broadcast be queued.
    canSendBroadcast () {
      return !!(this.broadcast.subject && this.broadcast.subject.trim() &&
                this.broadcast.message && this.broadcast.message.trim())
    }
  },
  created () {
    if (!this.$store.state.isSuperAdmin) return
    // Default the preview recipient to the signed-in admin's email.
    const cu = this.$store.state.currentUser
    if (cu && cu.email) this.previewTo = cu.email
    this.loadLegacyTotals()
    this.loadUsage()
    this.subscribeBroadcasts()
  },
  beforeDestroy () {
    if (this.broadcastsUnsub) this.broadcastsUnsub()
  },
  methods: {
    // ---- Email broadcasts -------------------------------------------
    // The Confirm modal emits `confirmed` with true (proceed) / false
    // (cancel). On proceed we write a broadcasts/{id} doc; the
    // processBroadcast Cloud Function (onCreate) re-verifies the author
    // is a super-admin and does the actual SendGrid sending.
    sendBroadcast (confirmed) {
      this.confirmBroadcast = false
      if (!confirmed) return
      if (!this.canSendBroadcast || this.sendingBroadcast) return
      const uid = this.$store.state.currentUser && this.$store.state.currentUser.uid
      if (!uid) {
        this.broadcastResult = 'Sign in required.'
        return
      }
      this.sendingBroadcast = true
      this.broadcastResult = 'Queuing…'
      const payload = {
        createdBy: uid,
        subject: this.broadcast.subject.trim(),
        message: this.broadcast.message.trim(),
        status: 'queued',
        createdOn: new Date()
      }
      if (this.broadcast.ctaUrl && this.broadcast.ctaUrl.trim()) {
        payload.ctaUrl = this.broadcast.ctaUrl.trim()
        payload.ctaLabel = (this.broadcast.ctaLabel && this.broadcast.ctaLabel.trim()) || 'Learn more'
      }
      fb.db.collection('broadcasts').add(payload).then(() => {
        this.sendingBroadcast = false
        this.broadcastResult = 'Queued — sending in the background. Watch "Recent sends" below.'
        this.broadcast = { subject: '', message: '', ctaUrl: '', ctaLabel: '' }
      }).catch(err => {
        this.sendingBroadcast = false
        this.broadcastResult = (err && err.message) || 'Failed to queue the update.'
      })
    },
    // Send a single "[Preview]" copy to one address (defaults to the
    // admin). Calls the sendBroadcastPreview onCall function directly
    // via fetch + Bearer token — same workaround as loadUsage, since
    // the v7 callable SDK can throw `internal` before any network call.
    async sendPreview () {
      if (!this.canSendBroadcast || this.sendingPreview) return
      const to = (this.previewTo || '').trim()
      if (!to) {
        this.broadcastResult = 'Enter a preview recipient email.'
        return
      }
      this.sendingPreview = true
      this.broadcastResult = 'Sending preview…'
      try {
        const user = firebase.auth().currentUser
        if (!user) throw new Error('Sign in required.')
        const idToken = await user.getIdToken()
        const url = 'https://us-central1-efficient-vineyard.cloudfunctions.net/sendBroadcastPreview'
        const resp = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + idToken
          },
          body: JSON.stringify({
            data: {
              subject: this.broadcast.subject,
              message: this.broadcast.message,
              ctaUrl: this.broadcast.ctaUrl,
              ctaLabel: this.broadcast.ctaLabel,
              to
            }
          })
        })
        if (!resp.ok) {
          const errBody = await resp.text()
          let msg = `HTTP ${resp.status}`
          try {
            const parsed = JSON.parse(errBody)
            if (parsed && parsed.error) msg = parsed.error.message || parsed.error.status || msg
          } catch { msg += `: ${errBody.slice(0, 200)}` }
          throw new Error(msg)
        }
        const json = await resp.json()
        const sentTo = (json.result && json.result.to) || to
        this.broadcastResult = `Preview sent to ${sentTo}.`
      } catch (err) {
        this.broadcastResult = (err && err.message) || 'Failed to send preview.'
      } finally {
        this.sendingPreview = false
      }
    },
    subscribeBroadcasts () {
      this.broadcastsUnsub = fb.db.collection('broadcasts')
        .orderBy('createdOn', 'desc')
        .limit(10)
        .onSnapshot(snap => {
          const rows = []
          snap.forEach(doc => {
            const d = doc.data()
            d.id = doc.id
            rows.push(d)
          })
          this.recentBroadcasts = rows
        }, err => {
          console.error('broadcasts subscription error:', err && err.message)
        })
    },
    broadcastBadge (status) {
      switch (status) {
        case 'sent': return 'badge-success'
        case 'sending': return 'badge-info'
        case 'queued': return 'badge-secondary'
        case 'failed':
        case 'rejected': return 'badge-danger'
        default: return 'badge-light'
      }
    },
    formatBroadcastTime (ts) {
      if (!ts) return '—'
      // Firestore Timestamp (client SDK) has toDate(); a JS Date passes
      // through; a serialized {seconds} shape is handled too.
      let d
      if (typeof ts.toDate === 'function') d = ts.toDate()
      else if (ts.seconds != null) d = new Date(ts.seconds * 1000)
      else d = new Date(ts)
      if (isNaN(d.getTime())) return '—'
      return d.toLocaleString()
    },
    formatN (n) {
      const v = Number(n) || 0
      if (v >= 1000000) return (v / 1000000).toFixed(1) + 'M'
      if (v >= 1000) return (v / 1000).toFixed(1) + 'k'
      return Math.round(v).toString()
    },
    formatBytes (n) {
      const v = Number(n) || 0
      if (v < 1024) return v + ' B'
      if (v < 1024 * 1024) return (v / 1024).toFixed(1) + ' KB'
      if (v < 1024 * 1024 * 1024) return (v / (1024 * 1024)).toFixed(1) + ' MB'
      if (v < 1024 * 1024 * 1024 * 1024) return (v / (1024 * 1024 * 1024)).toFixed(2) + ' GB'
      return (v / (1024 * 1024 * 1024 * 1024)).toFixed(2) + ' TB'
    },
    barWidth (credits) {
      return Math.max(0, Math.min(100, (credits / this.maxDailyCredits) * 100))
    },
    loadLegacyTotals () {
      fb.statisticsCollection.orderBy('createdOn', 'desc').limit(30).get().then(snap => {
        snap.forEach(item => {
          const stat = item.data()
          stat.id = item.id
          this.dailyStats.push(stat)
        })
        this.legacyTotals = this.dailyStats[0]
      }).catch(err => { console.log(err.message) })
    },
    async loadUsage (force) {
      if (this.usageLoading && !force) return
      this.usageLoading = true
      this.usageError = null
      try {
        // Bypass firebase.functions().httpsCallable() and POST directly
        // with a Bearer token. The v7 namespaced callable SDK has an
        // intermittent failure mode where it throws `code: 'internal'`
        // before ever issuing a network request — we hit it with
        // getRasterTileSource a while back and the same workaround
        // applies. Server-side onCall code is identical; the envelope
        // shape `{data: {...}}` -> `{result: {...}}` is what callables
        // wire-protocol expects, so we match it here.
        const user = firebase.auth().currentUser
        if (!user) throw new Error('Sign in required.')
        const idToken = await user.getIdToken()
        const url = 'https://us-central1-efficient-vineyard.cloudfunctions.net/getAdminUsageStats'
        const resp = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + idToken
          },
          body: JSON.stringify({ data: { days: 30, topN: 10 } })
        })
        if (!resp.ok) {
          const errBody = await resp.text()
          // Surface enough to debug: status code + parsed error message.
          let msg = `HTTP ${resp.status}`
          try {
            const parsed = JSON.parse(errBody)
            if (parsed && parsed.error) {
              msg += `: ${parsed.error.message || parsed.error.status || JSON.stringify(parsed.error)}`
            } else {
              msg += `: ${errBody.slice(0, 200)}`
            }
          } catch { msg += `: ${errBody.slice(0, 200)}` }
          // 404 here almost always means the function isn't deployed yet.
          if (resp.status === 404) {
            msg = 'getAdminUsageStats is not deployed. Run `firebase deploy --only functions:getAdminUsageStats` to publish it.'
          }
          throw new Error(msg)
        }
        const json = await resp.json()
        this.usage = json.result || {}
      } catch (err) {
        console.error('Admin stats error:', err)
        this.usageError = err && err.message ? err.message : 'Failed to load usage stats.'
      } finally {
        this.usageLoading = false
      }
    },
    downloadGIS () {
      const ref = firebase.storage().ref('statistics/farms/farms.geojson')
      ref.getDownloadURL().then(url => {
        fetch(url).then(r => r.blob()).then(blob => {
          const link = document.createElement('a')
          link.href = URL.createObjectURL(blob)
          link.download = 'farms.geojson'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          URL.revokeObjectURL(link.href)
        }).catch(e => console.error('Download error:', e))
      })
    },
    downloadUsers () {
      function exportToCsv (filename, rows) {
        let csv = ''
        rows.forEach(row => {
          csv += row.map(v => {
            const s = v == null ? '' : String(v).replace(/"/g, '""')
            return /[",\n]/.test(s) ? `"${s}"` : s
          }).join(',') + '\n'
        })
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = filename
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
      const rows = [['email', 'firstName', 'lastName']]
      fb.usersCollection.get().then(snap => {
        snap.forEach(item => {
          const d = item.data()
          rows.push([d.email || '', d.firstName || '', d.lastName || ''])
        })
        exportToCsv('users.csv', rows)
      }).catch(err => { console.log(err.message) })
    }
  }
}
</script>

<style scoped>
#admin {
  color: #eee;
  min-height: 100vh;
  padding-bottom: 60px;
  background: #1f2730;
}
#admin h1 { font-size: 26px; margin-bottom: 24px; }
#admin h2 {
  font-size: 17px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #aab;
  margin-bottom: 12px;
}
#admin h2 i { color: #ffc107; margin-right: 6px; }
#admin a { color: #ffc107; }

.admin-card {
  background: #2a313b;
  border: 1px solid #38404b;
  border-radius: 8px;
  padding: 16px 18px;
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
}
.stat-cell {
  background: #1f262e;
  border-radius: 6px;
  padding: 10px 12px;
}
.stat-num {
  font-size: 1.6rem;
  font-weight: 700;
  color: #ffc107;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
}
.stat-label {
  font-size: 0.72rem;
  color: #aab;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-top: 4px;
}

.plan-pill {
  display: inline-block;
  background: #1f262e;
  color: #ffc107;
  border-radius: 12px;
  padding: 1px 10px;
  font-size: 0.78rem;
  margin-left: 4px;
  font-variant-numeric: tabular-nums;
}

.trend-rows { display: flex; flex-direction: column; gap: 2px; }
.trend-row {
  display: grid;
  grid-template-columns: 90px 1fr 180px;
  align-items: center;
  gap: 10px;
  font-size: 0.82rem;
  padding: 3px 0;
}
.trend-date { color: #aab; font-variant-numeric: tabular-nums; }
.trend-bar {
  background: #1f262e;
  border-radius: 3px;
  height: 14px;
  overflow: hidden;
}
.trend-bar-fill {
  background: linear-gradient(90deg, #066739, #ffc107);
  height: 100%;
  transition: width 0.25s;
}
.trend-stats { color: #cce; font-variant-numeric: tabular-nums; }
.trend-sub { color: #889; margin-left: 4px; font-size: 0.74rem; }

.admin-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}
.admin-table th, .admin-table td {
  padding: 6px 10px;
  border-bottom: 1px solid #38404b;
  text-align: left;
}
.admin-table th { color: #aab; text-transform: uppercase; font-size: 0.72rem; letter-spacing: 0.04em; }
.admin-table .num { text-align: right; font-variant-numeric: tabular-nums; }
.admin-table tbody tr:hover { background: #1f262e; }
.text-muted { color: #889 !important; }
.text-warning { color: #ffc107 !important; }
</style>
